// app-routing.module.ts
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthComponent } from './components/auth/auth.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { AuthGuard } from './components/Guards/authGuard';
import { EventArtistGuard } from './components/Guards/event-artist.guard';
import { UserRoles } from './app.enums';
import { DsSectionHubComponent } from './common/ds-shell/section-hub/ds-section-hub.component';
import { SELLING_HUB, WEBSITE_HUB, SOCIAL_ACCOUNTS_HUB, LINK_SOCIALS_HUB, GALLERY_SELLING_HUB, GALLERY_WEBSITE_HUB } from './common/ds-shell/section-hub/section-hub.config';
import { ConnectionRequestsComponent } from './components/artist-components/connection-requests/connection-requests.component';
import { DispatchComponent } from './components/artist-components/dispatch/dispatch.component';
import { EditprofileComponent } from './common/editprofile/editprofile.component';
import { ConnectionRequestComponent } from './components/connection-request/connection-request.component';
import { ConsignmentProductSelectionComponent } from './components/gallery-components/consignment-product-selection/consignment-product-selection.component';
import { SignupComponent } from './components/signup/signup.component';
import { ViewArtistComponent } from './components/gallery-components/view-artist/view-artist.component';
import { ArtistViewProfileComponent } from './components/artist-components/view-artist-profile/view-artist-profile.component';
import { StripeConnectComponent } from './common/stripe-connect/stripe-connect.component';
import { ViewUserInvoiceComponent } from './common/view-user-invoice/view-user-invoice.component';
import { RegistrationComponent } from './components/registration/registration.component';
import { ViewArtworkComponent } from './common/view-artwork/view-artwork.component';
import { ViewConsignmentsComponent } from './components/artist-components/view-consignments/view-consignments.component';
import { AccountDeleteComponent } from './common/account-deletion/account-delete.component';
import { ForgotPasswordComponent } from './common/forgot-password/forgot-password.component';
import { SocialAccountComponent } from './common/social-account/social-account.component';
import { ConnectWithFacebook } from './common/connect-with-facebook/connect-with-facebook.component';
import { AppChatBox } from './common/chat-box/chat-box.component';
import { ConnectWithInstagram } from './common/connect-with-instagram/connect-with-instagram.component';
import { ArtistOnboardingCheckListComponent } from './components/artist-components/artist-onboarding-check-list/artist-onboarding-checklist.component';
import { GalleryOnboardingChecklistComponent } from './components/gallery-components/gallery-onboarding-checklist/gallery-onboarding-checklist.component';
import { ProductSocialMediaSharesComponent } from './common/product-social-media-shares/product-social-media-shares.component';
import { SharedListProductsComponent } from './components/shared-list-products/shared-list-products.component';
import { ConnectSquareupPosAccountComponent } from './components/connect-squareup-pos-account/connect-squareup-pos-account.component';
import { ShopifyAccount } from './components/gallery-components/shopify-account/shopify-account.component';
import { ConnectWoocommerceAccountComponent } from './components/gallery-components/connect-woocommerce-account/connect-woocommerce-account.component';
import { WixComponent } from './components/connect-with-wix/wix.component';
import { PlanGuard } from './components/Guards/planGuard';
import { InstagramAccountComponent } from './common/instagram-account/instagram-account.component';
import { DeleteMetaDataComponent } from './components/delete-meta-data/delete-meta-data.component';
import { DeleteInstagramMetaDataComponent } from './components/delete-instagram-meta-data/delete-instagram-meta-data.component';
import { NotificationsComponent } from './common/notifications/notifications.component';
import { NotificationListComponent } from './components/notification-lists/notification-lists.component';
import { AcceptInvitationComponent } from './components/accept-invitation/accept-invitation.component';
import { ProductSyncComponent } from './components/gallery-components/products-sync/product-sync.component';
import { SyncProductsArtistComponent } from './components/artist-components/sync-products-artist/sync-products-artist.component';
import { AuthorizedShopifyComponent } from './components/authorized-shopify/authorized-shopify.component';
import { SquareSpace } from 'src/app/common/SquareSpace/square-space.component';
import { LabelsGenerationComponent } from './components/gallery-components/labels-generation/labels-generation.component';
import { GalleryProposalComponent } from './components/gallery-components/gallery-proposal/gallery-proposal.component';
import { DsArtistDashboardComponent } from './components/artist-components/ds-artist-dashboard/ds-artist-dashboard.component';
import { AllPagesComponent } from './components/all-pages/all-pages.component';
import { DsGalleryDashboardComponent } from './components/gallery-components/ds-gallery-dashboard/ds-gallery-dashboard.component';
import { DsAdminDashboardComponent } from './components/admin-level/ds-admin-dashboard/ds-admin-dashboard.component';

const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'login', component: AuthComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'request-deletion', component: AccountDeleteComponent },
  { path: 'registration', component: RegistrationComponent },
  { path: 'signup/:invitationId', component: SignupComponent },
  { path: 'accept-external-invitation/:invitationId', component: AcceptInvitationComponent },
  {
    // Image Studio proof-of-concept (Simple | Advanced). Lazy-loaded so it stays
    // out of the main bundle. View at /image-studio-poc (optionally ?src=<image url>).
    path: 'image-studio-poc',
    loadComponent: () =>
      import('./components/artist-components/image-studio-poc/image-studio-poc.component')
        .then((m) => m.ImageStudioPocComponent),
  },
  {
    // Website Builder — the artist edits their site on the page itself. Lazy-loaded so it
    // stays out of the main bundle. Guarded: the POC route had no guard, so a lapsed session
    // loaded a working-looking builder whose every API call then 401'd.
    path: 'website-builder',
    loadComponent: () =>
      import('./components/artist-components/website-builder/website-builder.component')
        .then((m) => m.WebsiteBuilderComponent),
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist, UserRoles.Administrator, UserRoles.SuperAdmin] }
  },
  {
    path: 'stripe-connect',
    component: StripeConnectComponent
  },
  {
    path: 'password-forget/:id/:code',
    component: ForgotPasswordComponent
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator, UserRoles.Artist, UserRoles.Gallery, UserRoles.GalleryStaff] }
  },
  {
    path: 'ds-dashboard',
    component: DsArtistDashboardComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist] }
  },
  {
    // Phase-2 nav: artist "Selling" hub — a card landing for the selling tools + sales channels.
    path: 'selling',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist], hub: SELLING_HUB }
  },
  {
    // Phase-2 nav: artist "Website" hub — ArtFlow builder + public portfolio + external-platform links.
    path: 'website',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist], hub: WEBSITE_HUB }
  },
  {
    // Phase-2 nav: Marketing drawer → "Show Social Accounts" (FB + IG accounts). Artist + gallery.
    path: 'social-accounts',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist, UserRoles.Gallery, UserRoles.GalleryStaff], hub: SOCIAL_ACCOUNTS_HUB }
  },
  {
    // Phase-2 nav: Marketing drawer → "Link Your Socials" (connect FB + IG). Artist + gallery.
    path: 'link-socials',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist, UserRoles.Gallery, UserRoles.GalleryStaff], hub: LINK_SOCIALS_HUB }
  },
  {
    // Phase-2 nav (gallery): Selling hub — consignments + product sync + sales-channel connectors.
    path: 'gallery-selling',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery], hub: GALLERY_SELLING_HUB }
  },
  {
    // Phase-2 nav (gallery): Website hub — gallery portfolio + shareable products.
    path: 'gallery-website',
    component: DsSectionHubComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery], hub: GALLERY_WEBSITE_HUB }
  },
  {
    // Phase-2 nav: Messages section — incoming gallery connection requests (accept / decline).
    path: 'connection-requests',
    component: ConnectionRequestsComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist] }
  },
  {
    // Phase-2 nav: Messages section — consignments awaiting dispatch.
    path: 'dispatch',
    component: DispatchComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist] }
  },
  {
    // TEMPORARY nav-migration directory (role-aware: artist/gallery/admin). Remove when the new nav covers every page.
    path: 'all-pages',
    component: AllPagesComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Artist, UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.SuperAdmin, UserRoles.Administrator] }
  },
  {
    path: 'ds-gallery-dashboard',
    component: DsGalleryDashboardComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.SuperAdmin, UserRoles.Administrator] }
  },
  {
    path: 'ds-admin-dashboard',
    component: DsAdminDashboardComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator] }
  },
  {
    path: 'admin',
    loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator] }
  },
  {
    path: 'artists',
    loadChildren: () => import('./modules/artists/artists.module').then(m => m.ArtistsModule),
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator, UserRoles.Artist] }
  },
  {
    path: 'galleries',
    loadChildren: () => import('./modules/galleries/galleries.module').then(m => m.GallerysModule),
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator, UserRoles.Gallery, UserRoles.GalleryStaff] }
  },
  //COMPETITION MODULE
  {
    path: 'events',
    loadChildren: () => import('./modules/competition/competition.module').then(m => m.CompetitionModule),
    // canActivate: [AuthGuard],
    // data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator, UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist] }
  },
  // JUDGE MODULE (Judge is GalleryStaff with GalleryStaffPosition='Judge')
  {
    path: 'judge',
    loadChildren: () => import('./modules/judge/judge.module').then(m => m.JudgeModule),
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.GalleryStaff, UserRoles.SuperAdmin, UserRoles.Administrator] }
  },
  // CURATOR MODULE (Curator is GalleryStaff with GalleryStaffPosition='Curator')
  {
    path: 'curator',
    loadChildren: () => import('./modules/curator/curator.module').then(m => m.CuratorModule),
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.GalleryStaff, UserRoles.SuperAdmin, UserRoles.Administrator] }
  },

  {
    path: ':artistId/artworks/:artworkId',
    component: ViewArtworkComponent,
    //canActivate: [AuthGuard],
    //data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist, UserRoles.Administrator,] }
  },
  {
    path: ':artistId/shareable-product/:linkid',
    component: SharedListProductsComponent,
  },
  {
    path: ':galleryId/gallery-shareable-product/:linkid',
    component: SharedListProductsComponent,
  },
  {
    path: 'connection-request/:connectionId',
    component: ConnectionRequestComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist,] }

  },

  {
    path: 'view-proposals',
    component: GalleryProposalComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist] }
  },
  {
    path: 'view-consignments',
    component: ViewConsignmentsComponent,
    canActivate: [AuthGuard, EventArtistGuard],
    // data: { allowedRoles: [UserRoles.Administrator] }
  },
  {
    path: 'facebook-accounts',
    component: SocialAccountComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist,] }
  },
  {
    path: 'inbox',
    component: AppChatBox,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist,] }
  },
  {
    path: 'inbox/:id',
    component: AppChatBox,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist,] }
  },
  {
    path: 'connect-with-facebook',
    component: ConnectWithFacebook,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist,] }

  },
  {
    path: 'connect-with-instagram',
    component: ConnectWithInstagram,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist,] }

  },

  {
    path: 'connect-with-shopify-account',
    component: ShopifyAccount,
    canActivate: [AuthGuard, PlanGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'authorized-shopify', component: AuthorizedShopifyComponent

  },
  {
    path: 'connect-squareup-pos-account',
    component: ConnectSquareupPosAccountComponent,
    canActivate: [AuthGuard, PlanGuard],
    data: { allowedRoles: [UserRoles.Gallery,] }

  },
  {
    path: 'connect-with-squarespace-account',
    component: SquareSpace,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'connect-with-wix-account',
    component: WixComponent,
    canActivate: [AuthGuard, PlanGuard],
    data: { allowedRoles: [UserRoles.Gallery,] }

  },
  {
    path: 'connect-with-woocommerce-account',
    component: ConnectWoocommerceAccountComponent,
    canActivate: [AuthGuard, PlanGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }

  },

 


  {
    path: 'product-sync',
    component: ProductSyncComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery] }
  },
  {
    path: 'sync-products',
    component: SyncProductsArtistComponent,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Artist] }
  },
  {
    path: 'edit-profile',
    component: EditprofileComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.SuperAdmin, UserRoles.Administrator, UserRoles.Artist, UserRoles.Gallery, UserRoles.GalleryStaff] }
  },

  {
    // Public promotional wall. Anonymous by design — it shows only work whose artist opted in
    // (IsPubliclyDiscoverable + IsPubliclyListed), which is what the discovery feed it is built
    // from already enforces.
    path: 'discover',
    loadComponent: () =>
      import('./common/ds-artwork-mosaic/ds-artwork-mosaic.component')
        .then((m) => m.DsArtworkMosaicComponent),
  },

{
    path: 'profile/:identifier',
    component: ArtistViewProfileComponent
  },

  {
    path: 'artist/:artistId',
    component: ViewArtistComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Administrator] }
  },

  
  {
    path: 'consignment/:consignmentId/consignment-product-selection',
    component: ConsignmentProductSelectionComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff] }
  },
  {
    path: 'user-invoice',
    component: ViewUserInvoiceComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.GalleryStaff, UserRoles.Artist] }
  },
  {
    path: 'artist-onboarding-checklist',
    component: ArtistOnboardingCheckListComponent,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Artist] }
  },
  {
    path: 'gallery-onboarding-checklist',
    component: GalleryOnboardingChecklistComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery] }
  },
  {
    path: 'social-media',
    component: ProductSocialMediaSharesComponent,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'instagram-account',
    component: InstagramAccountComponent,
    canActivate: [AuthGuard, EventArtistGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'notifications-list',
    component: NotificationListComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'delete-facebook-meta-data',
    component: DeleteMetaDataComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'delete-instagram-meta-data',
    component: DeleteInstagramMetaDataComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  },
  {
    path: 'labels-generation',
    component: LabelsGenerationComponent,
    canActivate: [AuthGuard],
    data: { allowedRoles: [UserRoles.Gallery, UserRoles.Artist] }
  }



];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    initialNavigation: 'enabledBlocking'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }